// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Route table for the University Control Center.
 *
 * Every area required by `docs/04-REPOSITORY_GUIDE.md` has a
 * route, and the table is derived from the same inventory the navigation uses,
 * so a required area cannot be present in one and missing from the other
 * (`CST-006`: a required descendant may not be omitted).
 *
 * A route is not an authorization boundary. `ADM-014` prohibits treating a URL
 * or button visibility as authorization, so each screen re-checks its
 * capability affordance and the service authorises every request regardless.
 */

import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AccessScreen } from './access';
import { AiServicesScreen } from './ai-services';
import { AnalyticsScreen } from './analytics';
import { AuditScreen } from './audit';
import { DashboardScreen } from './dashboard';
import { DocumentsScreen } from './documents';
import { GovernanceScreen } from './governance';
import { KnowledgeScreen } from './knowledge';
import { MemoryScreen } from './memory';
import { MonitoringScreen } from './monitoring';
import { AiModelsScreen, AiUsageScreen } from './settings';
import { TenantsScreen } from './tenants';

/** One routed administration area. */
export interface AreaRoute {
  readonly path: string;
  readonly element: ReactNode;
}

/** The required control-plane routes, one per navigable screen. */
export const areaRoutes: readonly AreaRoute[] = [
  { path: '/', element: <DashboardScreen /> },
  { path: '/tenants', element: <TenantsScreen /> },
  { path: '/settings/ai-models', element: <AiModelsScreen /> },
  { path: '/settings/ai-usage', element: <AiUsageScreen /> },
  { path: '/ai-services', element: <AiServicesScreen /> },
  { path: '/knowledge', element: <KnowledgeScreen /> },
  { path: '/documents', element: <DocumentsScreen /> },
  { path: '/governance', element: <GovernanceScreen /> },
  { path: '/analytics', element: <AnalyticsScreen /> },
  { path: '/audit', element: <AuditScreen /> },
  { path: '/monitoring', element: <MonitoringScreen /> },
  { path: '/access', element: <AccessScreen /> },
  { path: '/memory', element: <MemoryScreen /> },
];

/** Render the control-plane routes. */
export function AppRoutes(): ReactNode {
  return (
    <Routes>
      {areaRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
